import {
  FileMark,
  PERIOD_PRESETS,
  PeriodSelect,
  ReviewBadge,
  documentTypeLabel,
  formatBytes,
  periodRange,
  resolveIstPeriod,
  uploaderLabel,
  useLoad,
  when,
  type PeriodValue,
  type ProDocClient,
  type SummaryActivity,
} from '@fiveox/prodoc-ui'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  PageHeader,
  Skeleton,
  SkeletonRegion,
  SkeletonText,
  TONE_TEXT,
  buttonClass,
  cn,
  formatDate,
  formatNumber,
} from '@fiveox/ui'
import { CloudUpload } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'

import { DailyChart } from '../components/daily-chart'
import { useOrg } from '../components/org-context'
import { useUpload } from '../components/upload-provider'
import { DOCUMENT, WORKSPACE, type Verb } from '../lib/access'
import { prodoc } from '../lib/api'
import { STORE_URL, APP_SLUG } from '../lib/config'

function greeting(date = new Date()): string {
  const hour = date.getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

/** At this share of the plan's storage the tile says so, in words as well as tone. */
const NEARLY_FULL = 0.9

/* ---------------------------------------------------------- the period */

/** The picker's value — the package's own shape (FR-54, D55). */
export type HomePeriod = PeriodValue

/** Home's period before anyone chose one: this month, as the screen always opened. */
export const DEFAULT_PERIOD: HomePeriod = { preset: 'this_month', from: '', to: '' }

/**
 * **Home's period is remembered per browser** (D55, the reading of FR-54's
 * "remembered per browser"): a convenience, so it lives in `localStorage` and
 * every read and write is guarded — a private window or blocked storage
 * simply opens on this month. The library's period lives in its address
 * instead, so its default stays *any time*.
 */
export const PERIOD_KEY = 'prodoc.home.period'

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** A remembered period, if it is one the picker offers — anything else is this month. */
export function readPeriod(): HomePeriod {
  try {
    const raw = JSON.parse(localStorage.getItem(PERIOD_KEY) ?? 'null') as Partial<HomePeriod> | null
    const preset = raw?.preset
    if (preset === 'custom') {
      const from = typeof raw?.from === 'string' && ISO_DAY.test(raw.from) ? raw.from : ''
      const to = typeof raw?.to === 'string' && ISO_DAY.test(raw.to) ? raw.to : ''
      return from && to && from <= to ? { preset, from, to } : DEFAULT_PERIOD
    }
    return typeof preset === 'string' && (PERIOD_PRESETS as readonly string[]).includes(preset)
      ? { preset: preset as HomePeriod['preset'], from: '', to: '' }
      : DEFAULT_PERIOD
  } catch {
    return DEFAULT_PERIOD
  }
}

function writePeriod(period: HomePeriod) {
  try {
    localStorage.setItem(PERIOD_KEY, JSON.stringify(period))
  } catch {
    /* private window or full storage: the period still applies, it is just not remembered */
  }
}

/**
 * The IST days a period asks the summary for (both ends included). A custom
 * range still being typed — one end only — keeps asking for this month until
 * it is a range; *any time* is the library's and never Home's.
 */
export function periodDays(period: HomePeriod, today?: string): { from: string; to: string } {
  const range = periodRange(period, today)
  if (range?.from && range.to) return range
  const { from, to } = resolveIstPeriod('this_month', today)
  return { from, to }
}

/** The register's `uploaded` filter for a period — the library's own address, so the tile's count is the register's. */
export function uploadedParams(period: HomePeriod): string {
  const p = new URLSearchParams()
  if (period.preset === 'custom') {
    p.set('uploaded', 'custom')
    if (period.from) p.set('uploaded_from', period.from)
    if (period.to) p.set('uploaded_to', period.to)
  } else if (period.preset !== 'any') p.set('uploaded', period.preset)
  return p.toString()
}

/**
 * Where a sign-in lands (FR-31): what is here and what came in lately, for the
 * reader's visible scope — **every number from `GET /summary`**, one read, so
 * the counts agree with the register by construction.
 *
 * **W22 (FR-57, D54) puts the pipeline first**: five tiles — *Uploaded* and
 * *Accepted* in the period the picker names (FR-54, remembered per browser),
 * *Reading*, *Needs review* and *Failed* as they stand now — each opening the
 * register filtered to what it counts; and the **daily chart** of the last 14
 * IST days, whatever the period, with a table under it. Every upload button
 * opens the upload popup over this screen (FR-59).
 *
 * The headline numbers are **stat tiles**, not charts — a handful of counts is
 * a KPI row. No delta or trend: the service keeps no history to draw one from.
 * Each tile is a door to the documents it counts. **Storage** is the
 * organisation's whole use against its plan's limit (app-management's
 * `storage` answer), a meter that says in words when it is nearly full and
 * links to the store. *By workspace* and *by type* are one series each — thin
 * bars in one hue, the value at the tip, every row a door into the register.
 */
export function HomePage() {
  const { org, me, can, userId } = useOrg()
  const { openUpload } = useUpload()
  return (
    <HomeScreen
      client={prodoc}
      orgName={org.display_name}
      firstName={me?.name?.split(' ')[0] ?? 'there'}
      can={can}
      userId={userId}
      onUpload={() => openUpload()}
    />
  )
}

/** The screen itself, over any client — the route hands it the app's. */
export function HomeScreen({
  client,
  orgName,
  firstName,
  can,
  userId,
  onUpload,
  today,
}: {
  client: ProDocClient
  orgName: string
  firstName: string
  can: (resource: string, verb: Verb) => boolean
  userId: string | null
  /** Opens the upload popup (FR-59) — the host's `openUpload`. */
  onUpload?: () => void
  /** The IST day the period counts from — today, unless a test fixes it. */
  today?: string
}) {
  const [period, setPeriod] = useState<HomePeriod>(readPeriod)
  const days = periodDays(period, today)
  const summary = useLoad(() => client.summary(days), [client, days.from, days.to])
  const choose = (next: HomePeriod) => {
    setPeriod(next)
    writePeriod(next)
  }
  // Names only — a count never waits for them, and their absence never fails the page.
  const workspaces = useLoad(() => client.workspaces.list(), [client])
  const options = useLoad(() => client.documents.options(), [client])
  const canUpload = can(DOCUMENT, 'can_create')
  // Deciding is `can_update` on `document` — the same verb `/review` is routed behind.
  const canReview = can(DOCUMENT, 'can_update')
  const s = summary.data
  const workspaceCount = workspaces.data?.filter((w) => w.status !== 'archived').length

  const header = (
    <PageHeader
      title={`${greeting()}, ${firstName}`}
      description={
        <>
          Working in <b className="text-foreground font-medium">{orgName}</b>
          {workspaceCount ? ` · ${workspaceCount} workspace${workspaceCount === 1 ? '' : 's'}` : ''}
        </>
      }
      actions={
        <>
          <PeriodSelect label="Period" value={period} onChange={choose} today={today} />
          {canUpload && (
            <Button onClick={onUpload} aria-haspopup="dialog">
              <CloudUpload size={18} strokeWidth={1.75} aria-hidden />
              Upload documents
            </Button>
          )}
        </>
      }
    />
  )

  if (summary.error && !s) {
    return (
      <div className="space-y-6">
        {header}
        <Alert tone="error">
          The overview could not be read: {summary.error}{' '}
          <Button variant="link" onClick={() => void summary.reload()}>
            Try again
          </Button>
        </Alert>
      </div>
    )
  }

  if (!s) {
    return (
      <div className="space-y-6">
        {header}
        <SkeletonRegion label="Loading the overview" className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Card key={i} className="space-y-3 p-5">
                <SkeletonText w="w-28" />
                <Skeleton className="h-8 w-16" />
                <SkeletonText w="w-36" />
              </Card>
            ))}
          </div>
          <Skeleton className="h-72 rounded-2xl" />
        </SkeletonRegion>
      </div>
    )
  }

  if (s.total === 0) {
    return (
      <div className="space-y-6">
        {header}
        <EmptyState
          title="No documents yet"
          description="Upload PDFs, scans, images, spreadsheets and Word documents. ProDoc stores each one in this organisation’s private store, filed in a workspace and a folder under the type you give it, and finds it again by title, type, tag, folder or owner app."
          action={
            canUpload ? (
              <Button variant="outline" onClick={onUpload} aria-haspopup="dialog">
                Upload your first documents
              </Button>
            ) : can(WORKSPACE, 'can_create') && (workspaces.data?.length ?? 0) === 0 ? (
              <Link to="/admin/workspaces" className={buttonClass({ variant: 'outline' })}>
                Set up workspaces
              </Link>
            ) : undefined
          }
        />
        <StorageTile used={s.storage.used_bytes} limit={s.storage.limit_bytes} />
      </div>
    )
  }

  const types = options.data?.document_types ?? []
  const workspaceName = (id: string) => workspaces.data?.find((w) => w.id === id)?.name ?? (workspaces.data ? 'An archived workspace' : 'A workspace')
  const byWorkspace = Object.entries(s.by_workspace).map(([id, count]) => ({ key: id, label: workspaceName(id), count, to: `/documents?workspace=${encodeURIComponent(id)}` }))
  const byType = Object.entries(s.by_type).map(([code, count]) => ({ key: code, label: documentTypeLabel(code, types), count, to: `/documents?type=${encodeURIComponent(code)}` }))

  const p = s.pipeline
  // The service's own days for the period, when it says them; the picker's otherwise.
  const span = s.period ?? days
  const inPeriod = span.from === span.to ? `On ${formatDate(span.from)}` : `${formatDate(span.from)} – ${formatDate(span.to)}`
  const uploaded = uploadedParams(period)

  return (
    <div className="space-y-6">
      {header}

      {/* The pipeline (FR-57): each tile opens the register filtered to what it counts. */}
      <section
        aria-label="Pipeline"
        aria-busy={summary.loading || undefined}
        className={cn('grid gap-4 transition-opacity sm:grid-cols-3 lg:grid-cols-5', summary.loading && 'opacity-60')}
      >
        <StatTile to={`/documents${uploaded ? `?${uploaded}` : ''}`} label="Uploaded" value={p.uploaded} note={inPeriod} />
        <StatTile to="/documents?reading=reading" label="Reading" value={p.reading} note={p.reading === 0 ? 'Nothing is being read now' : 'Being read now'} />
        <StatTile
          to="/documents?reading=needs_review"
          label="Needs review"
          value={p.needs_review}
          note={p.needs_review === 0 ? 'No reading waits for a reviewer' : 'A reading waits for a reviewer'}
        />
        <StatTile to={`/documents?reading=accepted${uploaded ? `&${uploaded}` : ''}`} label="Accepted" value={p.accepted} note={inPeriod} />
        <StatTile to="/documents?reading=failed" label="Failed" value={p.failed} note={p.failed === 0 ? 'Nothing failed to read' : 'Refused or failed — open one to see why'} />
      </section>

      <div className="grid items-start gap-6 lg:grid-cols-3">
        <Card className="min-w-0 lg:col-span-2">
          <CardHeader title="Daily uploads, approvals and pending" description="Last 14 days" />
          <CardBody>
            <DailyChart days={s.daily} />
          </CardBody>
        </Card>
        <div className="grid gap-4">
          <StatTile to="/documents" label="Documents" value={s.total} note="Every stored document you can see" />
          <StatTile
            to={canReview ? '/review' : '/documents?review=needs_review'}
            label="Awaiting a decision"
            value={s.awaiting_review}
            note={s.awaiting_review === 0 ? 'Nothing is waiting for a decision' : canReview ? 'Waiting for a reviewer — open the queue' : 'Waiting for a reviewer’s decision'}
          />
          <StorageTile used={s.storage.used_bytes} limit={s.storage.limit_bytes} />
        </div>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-5">
        <Card className="min-w-0 lg:col-span-3">
          <CardHeader
            title="Recent uploads"
            action={
              <Link to="/documents" className={buttonClass({ variant: 'outline', size: 'sm' })}>
                All documents
              </Link>
            }
          />
          <CardBody>
            {s.recent_uploads.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nothing uploaded yet that you can see.</p>
            ) : (
              <ul className="divide-border divide-y" aria-label="Recent uploads">
                {s.recent_uploads.map((row) => (
                  <li key={row.id} className="flex items-center gap-3 py-3">
                    <FileMark mediaType={row.media_type} size="sm" />
                    <div className="min-w-0 flex-1">
                      <Link to={`/documents/${row.id}`} className="text-foreground block truncate text-sm font-medium hover:underline">
                        {row.title}
                      </Link>
                      <p className="text-muted-foreground text-xs">
                        {[
                          documentTypeLabel(row.document_type, types),
                          row.workspace_id ? workspaceName(row.workspace_id) : null,
                          row.owner_app && row.owner_app !== APP_SLUG ? row.owner_app : null,
                          row.created_at ? `${uploaderLabel(row.uploaded_by ?? '', userId)}, ${when(row.created_at)}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    <ReviewBadge state={row.review_state} size="sm" />
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card className="min-w-0 lg:col-span-2">
          <CardHeader title="Recent activity" description="The latest changes and reads in what you can see." />
          <CardBody>
            {s.recent_activity.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nothing has happened yet.</p>
            ) : (
              <ol className="space-y-3" aria-label="Recent activity">
                {s.recent_activity.map((a) => (
                  <li key={a.id} className="border-subtle border-l-2 pl-3">
                    <p className="text-foreground text-sm">
                      {actorOf(a, userId)} {ACTIVITY[a.action ?? ''] ?? 'changed'}{' '}
                      {a.document_id ? (
                        <Link to={`/documents/${a.document_id}`} className="font-medium hover:underline">
                          {a.document_title ?? 'a document'}
                        </Link>
                      ) : (
                        (a.document_title ?? '')
                      )}
                    </p>
                    {a.occurred_at && <p className="text-muted-foreground text-xs">{when(a.occurred_at)}</p>}
                  </li>
                ))}
              </ol>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-2">
        <Bars title="By workspace" description="Every document you can see, by where it is filed." rows={byWorkspace} empty="Nothing filed yet." />
        <Bars title="By type" description="Every document you can see, by the type it was given." rows={byType} empty="No types yet." />
      </div>
    </div>
  )
}

/** What a line of activity says a person did. */
const ACTIVITY: Record<string, string> = {
  'document.uploaded': 'uploaded',
  'document.version_added': 'added a version of',
  'document.metadata_changed': 'edited',
  'document.updated': 'edited',
  'document.moved': 'moved',
  'document.archived': 'archived',
  'document.restored': 'restored',
  'document.review_requested': 'sent for review',
  'document.approved': 'approved',
  'document.rejected': 'rejected',
  'document.read': 'opened',
  'document.tags_changed': 'retagged',
  'document.linked': 'linked',
  'document.unlinked': 'unlinked',
  'document.duplicate_resolved': 'chose what to do with a copy of',
  // W22 (D54): what the engine read, and what a person decided about it (engine FR-E13 – FR-E23).
  'extraction.requested': 'asked ProDoc to read',
  'extraction.completed': 'finished reading',
  'extraction.failed': 'could not read',
  'extraction.refused': 'could not read',
  'extraction.corrected': 'corrected what was read from',
  'extraction.accepted': 'accepted what was read from',
  'extraction.rejected': 'rejected what was read from',
}

/** The credential the parser works under (`extraction_review.ENGINE`). */
const ENGINE_SERVICE = 'prodoc-engine'

function actorOf(a: SummaryActivity, userId: string | null): string {
  if (a.actor.user_id && userId && a.actor.user_id === userId) return 'You'
  if (a.actor.display_name) return a.actor.display_name
  // The engine's own acts — a reading finished, an automatic acceptance (D54) — are ProDoc's.
  if (a.actor.kind === 'app' && a.actor.service === ENGINE_SERVICE) return 'ProDoc'
  if (a.actor.kind === 'app') return a.actor.service ?? 'An app'
  if (a.actor.kind === 'link') return 'Someone with a share link'
  if (a.actor.kind === 'system') return 'ProDoc'
  return 'A member'
}

/** A headline number that is also the way to the documents it counts. The
 *  whole tile is the link, so the tile owns the focus ring (UI_SYSTEM §4). */
function StatTile({ to, label, value, note }: { to: string; label: string; value: number; note: string }) {
  return (
    <Link to={to} className="group focus-visible:ring-ring rounded-2xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none">
      <Card className="group-hover:border-primary/40 h-full p-5 transition-colors">
        <p className="text-muted-foreground text-sm">{label}</p>
        <p className="text-foreground mt-1 text-3xl font-semibold">{formatNumber(value)}</p>
        <p className="text-muted-foreground mt-1 text-xs">{note}</p>
      </Card>
    </Link>
  )
}

/**
 * Storage — the organisation's whole use against its plan's limit (FR-6,
 * FR-31). A meter, not a chart: one value against one ceiling. Nearly full
 * says so in words and tone, and links to the store; without a limit it says
 * the plan sets none.
 */
function StorageTile({ used, limit }: { used: number; limit: number | null }) {
  const share = limit ? Math.min(1, used / limit) : null
  const full = share !== null && share >= 1
  const nearly = share !== null && share >= NEARLY_FULL
  return (
    <Card className="h-full p-5">
      <p className="text-muted-foreground text-sm">Storage used</p>
      <p className="text-foreground mt-1 text-3xl font-semibold">{formatBytes(used)}</p>
      {limit !== null && share !== null ? (
        <>
          <div
            role="meter"
            aria-label="Storage used of the plan’s limit"
            aria-valuemin={0}
            aria-valuemax={limit}
            aria-valuenow={Math.min(used, limit)}
            aria-valuetext={`${formatBytes(used)} of ${formatBytes(limit)}`}
            title={`${formatBytes(used)} of ${formatBytes(limit)} (${Math.round(share * 100)}%)`}
            className="bg-muted mt-2 h-1.5 overflow-hidden rounded-full"
          >
            <div className="bg-primary h-full rounded-r-full" style={{ width: `${Math.max(share * 100, used > 0 ? 2 : 0)}%` }} />
          </div>
          <p className={cn('mt-1 text-xs', nearly ? TONE_TEXT.warning : 'text-muted-foreground')}>
            {full ? 'The plan’s limit is reached — new uploads wait for more room. ' : nearly ? 'Nearly full. ' : ''}
            of {formatBytes(limit)} for the organisation
            {nearly && (
              <>
                {' · '}
                <a href={`${STORE_URL}/catalog/${APP_SLUG}`} className="font-medium underline underline-offset-2">
                  Add storage
                </a>
              </>
            )}
          </p>
        </>
      ) : (
        <p className="text-muted-foreground mt-1 text-xs">For the whole organisation — the plan sets no limit.</p>
      )}
    </Card>
  )
}

/** One series, compared by magnitude: horizontal bars in one hue, each value
 *  at its tip, the exact figure on hover. Thin (12px), square at the
 *  baseline, rounded at the data end. Each row opens those documents. */
function Bars({ title, description, rows, empty }: { title: string; description: string; rows: { key: string; label: string; count: number; to: string }[]; empty: ReactNode }) {
  const sorted = [...rows].sort((a, b) => b.count - a.count)
  const max = Math.max(1, ...sorted.map((r) => r.count))
  const total = sorted.reduce((n, r) => n + r.count, 0)
  return (
    <Card className="min-w-0">
      <CardHeader title={title} description={description} />
      <CardBody>
        {sorted.length === 0 ? (
          <p className="text-muted-foreground text-sm">{empty}</p>
        ) : (
          <ul className="space-y-4" aria-label={title}>
            {sorted.map((r) => (
              <li key={r.key}>
                <Link
                  to={r.to}
                  title={`${r.label}: ${formatNumber(r.count)} of ${formatNumber(total)}`}
                  className="group focus-visible:ring-primary/40 block rounded-md focus-visible:ring-2 focus-visible:outline-none"
                >
                  <span className="text-foreground text-sm group-hover:underline">{r.label}</span>
                  <span className="mt-1.5 flex items-center gap-2">
                    {r.count > 0 && (
                      <span
                        aria-hidden
                        className="bg-primary block h-3 shrink-0 rounded-r-[4px] transition-opacity group-hover:opacity-80"
                        style={{ width: `calc((100% - 3rem) * ${r.count / max})`, minWidth: 4 }}
                      />
                    )}
                    <span className="text-muted-foreground text-xs">{formatNumber(r.count)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}
